import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  facilitiesService,
  WarehouseContact,
} from "../../../services/facilities.service";
import { WarehouseFormDialog } from "./WarehouseFormDialog";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
  QuickFilter,
} from "../../../../../app.interface";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";
interface SaveWarehouseData {
  _id?: string;
  name: string;
  address: string;
  contacts: WarehouseContact[];
}

export const WarehousesManager: React.FC = () => {
  const { user } = useAuth();
  const [warehouses, setWarehouses] = React.useState<IESDoc[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedWarehouse, setSelectedWarehouse] =
    React.useState<IESDoc | null>(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("name.keyword");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [selectedWarehouseDetails, setSelectedWarehouseDetails] =
    React.useState<IESDoc | null>(null);

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter warehouses by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
          description: "Currently active warehouses",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: "#DC2626",
          description: "Inactive warehouses",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort warehouses by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "warehouseId",
      header: "Warehouse ID",
      field: "warehouseId",
      type: "action",
      width: "180px",
      renderAction: (row) => {
        return (
          <Fragment>
            <Typography fontWeight={600}>
              {`W-${row.warehouseId.substring(0, 9).toUpperCase()}`}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      id: "name",
      header: "Warehouse Name",
      field: "name",
      filterField: "name.keyword",
      type: "text",
      sortable: true,
      width: "200px",
    },
    {
      id: "contact",
      header: "Warehouse Contact",
      field: "contact",
      type: "contact",
      width: "300px",
      contactName: "contactName",
      contactEmail: "contactEmail",
    },
    {
      id: "address",
      header: "Address",
      field: "address",
      type: "text",
      width: "300px",
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      filterField: "is_deleted",
      type: "status",
      width: "120px",
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: WithId & Record<string, any>) => (
        <div className="flex gap-2 justify-end">
          <Tooltip title="Edit Warehouse" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const originalWarehouse = warehouses.find(
                  (w) => w._id === row._id
                );
                handleOpenFormDialog(originalWarehouse);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Warehouse" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  React.useEffect(() => {
    loadWarehouses();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const response = await facilitiesService.listWarehouses({
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        ...filters,
      });
      setWarehouses(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      toast.error("Failed to load warehouses");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWarehouse = async (data: SaveWarehouseData) => {
    try {
      setSaving(true);
      if (selectedWarehouse) {
        await facilitiesService.updateWarehouse({
          ...data,
          _id: selectedWarehouse._id,
        });
        toast.success("Warehouse updated successfully");
      } else {
        await facilitiesService.createWarehouse({
          ...data,
        });
        toast.success("Warehouse created successfully");
      }
      handleCloseFormDialog();
      loadWarehouses();
    } catch (error) {
      toast.error("Error saving warehouse");
      console.error("Error saving warehouse:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWarehouse = async () => {
    if (!selectedWarehouse) return;
    try {
      setSaving(true);
      await facilitiesService.deleteWarehouse(selectedWarehouse._id);
      toast.success("Warehouse deleted successfully");
      handleCloseDeleteDialog();
      loadWarehouses();
    } catch (error) {
      toast.error("Failed to delete warehouse");
      console.error("Error deleting warehouse:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFormDialog = (warehouse?: IESDoc) => {
    setSelectedWarehouse(warehouse || null);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setSelectedWarehouse(null);
    setFormDialogOpen(false);
  };

  const handleOpenDeleteDialog = (warehouse: IESDoc) => {
    setSelectedWarehouse(warehouse);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedWarehouse(null);
    setDeleteDialogOpen(false);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    const warehouse = warehouses.find((w) => w._id === row._id);
    if (warehouse) {
      setSelectedWarehouseDetails(warehouse);
    }
  };

  const handleDrawerClose = () => {
    setSelectedWarehouseDetails(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Warehouse Management"
        subtitle="Manage your organization's warehouses"
        icon="warehouse"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => handleOpenFormDialog()}
            disabled={
              !hasPermission(
                "inventory.warehouses.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Warehouse
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search warehouses by name or address..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={warehouses.map((warehouse) => ({
            _id: warehouse._id,
            name: warehouse.name,
            address: warehouse.address,
            warehouseId: warehouse.warehouse_id,
            createdAt: warehouse.created_at,
            createdBy: warehouse.created_by_email,
            totalCost: warehouse.total_cost || 0,
            status: warehouse.is_deleted ? "inactive" : "active",
            contactName: warehouse.contacts?.[0]?.name || warehouse.name,
            contactEmail: warehouse.contacts?.[0]?.email || "N/A",
          }))}
          allowHeaderWrap
          columns={MasterColumns}
          variant="default"
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          loading={loading}
          header={{
            title: "Warehouses List",
            subtitle: "Your current list of warehouses",
          }}
          selectable
          onSelectionChange={(selected) => {
            console.log("Selected:", selected);
          }}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: () => loadWarehouses(),
            },
            export: {
              label: "Export Warehouses",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "warehouses-list",
              },
            },
          }}
          pagination={{
            total: totalItems,
            page: page,
            pageSize: pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          onRowClick={handleRowClick}
          rowProps={{
            sx: {
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            },
          }}
        />
      </Paper>

      {/* Warehouse Details Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedWarehouseDetails}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          },
        }}
      >
        {selectedWarehouseDetails ? (
          <Fragment>
            <Box
              sx={{
                px: 3,
                py: 2,
                background: "#0891B2",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Icon>warehouse</Icon>
                <Typography variant="subtitle1" fontWeight={600}>
                  Warehouse Details
                </Typography>
              </Box>
              <IconButton
                onClick={handleDrawerClose}
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Box display="grid" gap={3}>
                <DetailItem
                  icon="warehouse"
                  label="Warehouse Name"
                  value={selectedWarehouseDetails.name || "N/A"}
                />
                <DetailItem
                  icon="location_on"
                  label="Address"
                  value={selectedWarehouseDetails.address || "N/A"}
                />
                <DetailItem
                  icon="fiber_manual_record"
                  label="Status"
                  value={
                    selectedWarehouseDetails.is_deleted ? "Inactive" : "Active"
                  }
                  valueColor={
                    selectedWarehouseDetails.is_deleted ? "#DC2626" : "#059669"
                  }
                />
                <DetailItem
                  icon="schedule"
                  label="Created At"
                  value={
                    selectedWarehouseDetails.created_at
                      ? new Date(
                          selectedWarehouseDetails.created_at
                        ).toLocaleDateString()
                      : "N/A"
                  }
                />
                <DetailItem
                  icon="person"
                  label="Created By"
                  value={selectedWarehouseDetails.created_by_email || "N/A"}
                />
              </Box>

              {selectedWarehouseDetails.contacts &&
                selectedWarehouseDetails.contacts.length > 0 && (
                  <>
                    <Divider
                      sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }}
                    />

                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: "text.primary",
                        fontFamily: "Montserrat",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }}>contacts</Icon>
                      Contact Information
                    </Typography>

                    <Box display="grid" gap={3}>
                      {selectedWarehouseDetails.contacts.map(
                        (contact: WarehouseContact, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              borderRadius: 1,
                              bgcolor: alpha("#0891B2", 0.04),
                              border: "1px solid",
                              borderColor: alpha("#0891B2", 0.1),
                            }}
                          >
                            <DetailItem
                              icon="person"
                              label="Contact Name"
                              value={contact.name || "N/A"}
                            />
                            <Box sx={{ mt: 2 }} display="grid" gap={1.5}>
                              {contact.email && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    email
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`mailto:${contact.email}`}
                                  >
                                    {contact.email}
                                  </Typography>
                                </Box>
                              )}
                              {contact.phone && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    phone
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`tel:${contact.phone}`}
                                  >
                                    {contact.phone}
                                  </Typography>
                                </Box>
                              )}
                              {contact.whatsapp && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    whatsapp
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`https://wa.me/${contact.whatsapp}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {contact.whatsapp}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )
                      )}
                    </Box>
                  </>
                )}

              <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

              <Box display="flex" gap={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    handleOpenFormDialog(selectedWarehouseDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.warehouses.edit",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#0891B2",
                    color: "#0891B2",
                    "&:hover": {
                      borderColor: "#0E7490",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  Edit Warehouse
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Icon>delete</Icon>}
                  onClick={() => {
                    handleOpenDeleteDialog(selectedWarehouseDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.warehouses.delete",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#DC2626",
                    color: "#DC2626",
                    "&:hover": {
                      borderColor: "#B91C1C",
                      bgcolor: alpha("#DC2626", 0.04),
                    },
                  }}
                >
                  Delete Warehouse
                </Button>
              </Box>
            </Box>
          </Fragment>
        ) : null}
      </Drawer>

      <WarehouseFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSave={handleSaveWarehouse}
        warehouse={
          selectedWarehouse
            ? {
                _id: selectedWarehouse._id,
                name: selectedWarehouse.name,
                address: selectedWarehouse.address,
                contacts: selectedWarehouse.contacts || [],
              }
            : null
        }
        loading={saving}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteWarehouse}
        title="Delete Warehouse"
        itemName={selectedWarehouse?.name || ""}
        itemDescription={selectedWarehouse?.address || ""}
        loading={saving}
        icon="warehouse"
      />
    </Box>
  );
};

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
}> = ({ icon, label, value, valueColor }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: valueColor || "text.primary",
        fontFamily: "Montserrat",
      }}
    >
      {value}
    </Typography>
  </Box>
);
